<template>
  <el-container class="search-page">
    <el-main>
      <div class="search-header-container">
        <div class="input-wrapper">
          <el-input
            v-model="keyword"
            :placeholder="searchHint"
            class="custom-search-input"
            clearable
            @focus="focus"
            @input="inputEvent"
            @keyup.enter.native="handleOnSearch"
            @blur="onBlur"
          >
            <el-button slot="append" icon="el-icon-search" class="search-btn" @click="handleOnSearch" />
          </el-input>

          <transition name="el-zoom-in-top">
            <el-card v-if="isFocus" class="search-suggest-card shadow-hover">

              <div v-if="hotSearchList.length > 0" class="suggest-section">
                <div class="search-title">
                  <i class="el-icon-hot-water" /> 热门搜索
                </div>
                <div class="hot-tags-container">
                  <div
                    v-for="(hotSearch, index) in hotSearchList"
                    :key="'hot-'+index"
                    class="suggest-tag"
                    @mousedown="onClickHot(hotSearch.keyword)"
                  >
                    {{ hotSearch.keyword }}
                  </div>
                </div>
              </div>

              <div v-if="keyword !== '' && searchList.length > 0" class="suggest-section suggest-list-box">
                <div class="search-title">
                  <i class="el-icon-magic-stick" /> 搜索建议
                </div>
                <div class="suggest-list">
                  <div
                    v-for="(searchSuggest, index) in searchList"
                    :key="'sug-'+index"
                    class="suggest-list-item"
                    @mousedown="onClickHot(searchSuggest.value)"
                  >
                    <i class="el-icon-search" /> {{ searchSuggest.value }}
                  </div>
                </div>
              </div>
            </el-card>
          </transition>
        </div>
      </div>

      <div v-if="isFocus" class="search-mask" @click="blur" />

      <div class="result-container">
        <el-row v-loading="loading" :gutter="isMobile ? 8 : 14" class="movie-list">
          <el-col
            v-for="(video, index) in dataList"
            :key="'res-'+index"
            :lg="4"
            :md="6"
            :sm="8"
            :xs="12"
            class="video-col"
          >
            <search-video-card :video="video" />
          </el-col>
        </el-row>

        <div v-if="showEmpty && !loading" class="empty-result">
          <img src="@/assets/img/not-result.png" alt="无结果">
          <p>没有找到关于 "{{ keyword }}" 的内容</p>
        </div>

        <el-row v-if="dataList.length > 0" class="pagination-row">
          <el-pagination
            :small="isMobile"
            background
            layout="prev, pager, next"
            :page-size="pageSize"
            :current-page="currentPage"
            :total="totalSize"
            @current-change="handleCurrentChange"
          />
        </el-row>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import SearchVideoCard from '@/components/card/SearchVideoCard'
import { videoQuery, keywordSuggest, hotKeyword } from '@/api/vod'

export default {
  name: 'SearchIndex',
  components: { SearchVideoCard },
  data() {
    return {
      screenWidth: document.body.clientWidth,
      currentPage: 1,
      pageSize: 12,
      totalSize: 0,
      dataList: [],
      hotSearchList: [],
      searchList: [],
      searchHint: '搜索你感兴趣的内容',
      showEmpty: false,
      loading: false,
      keyword: '',
      isFocus: false, // 核心状态控制
      timer: null
    }
  },
  computed: {
    isMobile() { return this.screenWidth <= 768 }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(newQuery) {
        this.keyword = newQuery.keyword || ''
        this.currentPage = parseInt(newQuery.pn) || 1
        if (this.keyword) {
          this.searchHandler()
          document.title = '搜索 - ' + this.keyword
        }
      }
    }
  },
  mounted() {
    this.fetchHotKeywords()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() { this.screenWidth = document.body.clientWidth },
    fetchHotKeywords() {
      hotKeyword().then(resp => {
        if (resp.code === 0) this.hotSearchList = resp.data
      })
    },
    handleCurrentChange(p) {
      if (this.currentPage === p) return
      this.$router.push({
        path: '/vod/search',
        query: { keyword: this.keyword, pn: p }
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err
      })
    },
    handleOnSearch() {
      if (!this.keyword || !this.keyword.trim()) return
      this.isFocus = false
      this.$router.push({
        path: '/vod/search',
        query: { keyword: this.keyword.trim(), pn: 1 }
      })
    },
    searchHandler() {
      this.loading = true
      videoQuery(this.keyword, this.currentPage).then(resp => {
        if (resp.code === 0) {
          this.totalSize = resp.data.totalSize
          this.dataList = resp.data.list
          this.showEmpty = this.totalSize === 0
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }).finally(() => { this.loading = false })
    },
    focus() {
      this.isFocus = true
    },
    // 修复点 2: 延迟关闭，确保 mousedown 事件能先执行
    onBlur() {
      setTimeout(() => {
        this.isFocus = false
      }, 200)
    },
    blur() {
      this.isFocus = false
    },
    inputEvent() {
      if (!this.keyword) {
        this.searchList = []
        return
      }
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        keywordSuggest(this.keyword).then(resp => {
          if (resp.code === 0) {
            this.searchList = resp.data.map(item => ({ value: item.keyword }))
          }
        })
      }, 300)
    },
    onClickHot(val) {
      this.keyword = val
      this.handleOnSearch()
    }
  }
}
</script>

<style scoped lang="scss">
.search-page { background-color: #f6f8fa; min-height: 100vh; }
.search-header-container { max-width: 700px; margin: 15px auto 30px; padding: 0 10px; }
.input-wrapper { position: relative; z-index: 2001; }

.custom-search-input ::v-deep .el-input__inner {
  height: 48px; border-radius: 24px 0 0 24px; border: 2px solid #fe2c55; padding-left: 20px; font-size: 15px;
}
.custom-search-input ::v-deep .el-input-group__append {
  background-color: #fe2c55; border: 2px solid #fe2c55; border-radius: 0 24px 24px 0; color: #fff; padding: 0 25px; cursor: pointer;
}

.search-suggest-card {
  position: absolute; top: 55px; left: 0; width: 100%; border-radius: 16px; border: none; box-shadow: 0 12px 32px rgba(0,0,0,0.15); z-index: 2005;
}

.suggest-section {
  padding: 10px 5px;
  &:not(:last-child) {
    border-bottom: 1px solid #f2f6fc;
  }
}

.hot-tags-container { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 5px; }
.suggest-tag {
  padding: 6px 14px; background-color: #f4f4f5; border-radius: 18px; font-size: 13px; color: #606266; cursor: pointer;
  &:hover { background-color: #fe2c55; color: #fff; }
}

.suggest-list { margin-top: 5px; }
.suggest-list-item {
  padding: 10px 8px; font-size: 14px; color: #303133; cursor: pointer; border-radius: 8px;
  &:hover { background-color: #f5f7fa; color: #fe2c55; }
  i { margin-right: 8px; color: #909399; }
}

.search-mask { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.1); z-index: 1000; }
.result-container { max-width: 1400px; margin: 0 auto; padding: 0 10px; }
.video-col { margin-bottom: 15px; }
.pagination-row { display: flex; justify-content: center; margin: 40px 0; }

.search-title {
  font-size: 14px;
  color: #909399;
  font-weight: bold;
  margin-bottom: 8px;
  i { margin-right: 5px; color: #fe2c55; }
}

@media screen and (max-width: 768px) {
  .video-col { padding: 0 4px !important; }
}
</style>
